import { observer } from 'mobx-react'
import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { UploadAnimatedBar, UploadAnimatedBarWrapper, VaultDropAreaBody, VaultFileDropArea, VaultFileDropAreaDetails, ImportPersonalBalanceWrapper, UploadAnyWhiteWrapper, VaultDropAreaWrapper, IllustrationIconWrapper, IllustrationIconTextTopRow, IllustrationIconTextSecondRow } from './VaultDropArea.styles'
import VaultCover from '../../assets/vault-cover.png'
import { MembershipIcon, MobileDocsIcon, UploadIcon, IllustrationIcon, UploadIconBlack } from '../../assets/icons'
import EmptyStateBalanceSheetPhotoGrey from '../../assets/empty-state-balance-sheet-photo-grey.png'
import FilesLogos from '../../assets/files-logos.png'
import { MAX_VAULT_FILE_SIZE_MB, SUPPORTED_FILE_FORMATS } from '../Vault.const'
import { useStore } from '../../../app/data/root.store'
import { DemoStartPortfolioButton, PrimaryButtonWithIcon } from '../../../../common/components/buttons/NewButtons.styles'
import { LinkArrowIcon } from '../../../../assets/icons/common/common-icons'
import { VaultInfo } from './VaultInfo'


export const VaultDropArea = observer(({ isDontHaveBalanceSheetClicked }) => {
    const { vaultStore, uiStore, userStore } = useStore();
    const isDesktopView = uiStore.isDesktopView;
    const { isDemoMode, isScoreTier4, isReadOnlyMode, isWelcome } = userStore;

    const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => await vaultStore.upload(acceptedFiles, rejectedFiles, { directUpload: !isDesktopView }), [vaultStore, isDesktopView]);

    const maxSizeInBytes = (MAX_VAULT_FILE_SIZE_MB * (2 ** 20));
    const isDropzoneDisabled = vaultStore.isUploading;
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxSize: maxSizeInBytes,
        accept: SUPPORTED_FILE_FORMATS,
        disabled: isDropzoneDisabled,
        noClick: isDropzoneDisabled,
        noDrag: isDropzoneDisabled,
        noKeyboard: isDropzoneDisabled
    });

    // Get custom root props with additional handlers for when uploads are in progress or a file exists
    const rootProps = getRootProps();
    const customRootProps = {
        ...rootProps,
        onDrop: (e) => {
            rootProps.onDrop && rootProps.onDrop(e);
        }
    };

    return (
        <VaultDropAreaWrapper>
            {!isWelcome && <img src={VaultCover} alt="Vault" style={{ borderTopLeftRadius: 12, borderTopRightRadius: 12, maxWidth: '100%' }} />}
            {!isWelcome && <VaultInfo />}
            <VaultDropAreaBody
                isWelcome={isWelcome}
                isActivationFlow
                showPointer={!isDropzoneDisabled}
                isHoverActive={isDragActive && !isDropzoneDisabled}
                {...customRootProps}
            >
                <input {...getInputProps()} disabled={isDropzoneDisabled} />
                <VaultFileDropArea>

                    {
                        (isWelcome && isDesktopView) ?
                            <WelcomeDropArea
                                isUploading={vaultStore.isUploading}
                                isDontHaveBalanceSheetClicked={isDontHaveBalanceSheetClicked}
                                hasHandledFiles={vaultStore.hasHandledFiles}
                            />
                            :
                            <GetStartedDropArea
                                isDemoMode={isDemoMode}
                                isScoreTier4={isScoreTier4}
                                isReadOnlyMode={isReadOnlyMode}
                                isUploading={vaultStore.isUploading}
                                isDesktopView={isDesktopView}
                                customRootProps={customRootProps}
                                isWelcome={isWelcome}
                            />
                    }
                </VaultFileDropArea>
            </VaultDropAreaBody>
        </VaultDropAreaWrapper >
    )
});


const WelcomeDropArea = ({ isUploading, isDontHaveBalanceSheetClicked, hasHandledFiles }) => {
    return (
        <>
            <img src={VaultCover} alt="Vault" style={{ borderTopLeftRadius: 12, borderTopRightRadius: 12, maxWidth: '100%' }} />
            <img src={EmptyStateBalanceSheetPhotoGrey} alt="Empty State Balance Sheet" style={{ width: '100%', maxWidth: '464px', height: 'auto', marginTop: 24 }} />
            {
                isUploading ?
                    <UploadAnimatedBarWrapper>
                        <UploadAnimatedBar />
                    </UploadAnimatedBarWrapper>
                    :
                    <>
                        {
                            (isDontHaveBalanceSheetClicked || hasHandledFiles) ?
                                <UploadAnyWhiteWrapper>
                                    <UploadIconBlack />Upload any financial document
                                </UploadAnyWhiteWrapper>
                                :
                                <ImportPersonalBalanceWrapper>
                                    <img src={FilesLogos} alt="Files Logos" style={{ width: 55, height: 43 }} />
                                    Import Personal Balance Sheet / Spreadsheet Tracker
                                </ImportPersonalBalanceWrapper>
                        }
                    </>
            }
        </>
    )
};

const GetStartedDropArea = ({ isDemoMode, isScoreTier4, isReadOnlyMode, isUploading, isDesktopView, customRootProps }) => {
    return (
        <>
            <VaultFileDropAreaDetails>
                <IllustrationIconWrapper>
                    {isDesktopView ? <IllustrationIcon /> : <MobileDocsIcon />}
                </IllustrationIconWrapper>
                {
                    isUploading ?
                        <UploadAnimatedBarWrapper>
                            <UploadAnimatedBar />
                        </UploadAnimatedBarWrapper> :
                        <div
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                            {...customRootProps}
                        >
                            <>

                                <IllustrationIconTextTopRow>{isDesktopView ? 'Drag and Drop Here:' : `Upload Spreadsheet / K-1 / PFS / PPM etc.`}</IllustrationIconTextTopRow>
                                <IllustrationIconTextSecondRow>
                                    {
                                        isDesktopView ? `Spreadsheet / K-1 / PFS / PPM etc.` :
                                            <>
                                                Include documents that contain your investment terms, history and future cash flow.<br />
                                                Vyzer will automatically update your performance.
                                            </>
                                    }
                                </IllustrationIconTextSecondRow>
                            </>

                        </div>
                }
            </VaultFileDropAreaDetails>

            {
                !isUploading &&
                <>
                    {
                        isDemoMode ?
                            <DemoStartPortfolioButton style={{ marginTop: '16px' }}>
                                {isScoreTier4 ? 'Start my portfolio' : 'Try import with AI for free'}<LinkArrowIcon />
                            </DemoStartPortfolioButton> :
                            <PrimaryButtonWithIcon>
                                {isReadOnlyMode ?
                                    <><MembershipIcon /> Unlock with membership</>
                                    :
                                    <><UploadIcon />Upload any financial document</>}
                            </PrimaryButtonWithIcon>
                    }
                </>
            }
        </>
    )
}